import React, { useEffect, useState } from "react";
import { RxCross1 } from "react-icons/rx";
import styles from "../../styles/styles";
import { Link, useNavigate } from "react-router-dom";
import { IoBagHandleOutline } from "react-icons/io5";
import { HiOutlineMinus, HiPlus } from "react-icons/hi";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import { backend_url, server } from "../../server";
import { addTocart, removeBundleDealFromCart, removeFromCart } from "../../redux/actions/cart";
import BundleRemovalWarningModal from "../Layout/BundleRemovalWarningModal";
import {
  sumCartSubtotal,
  sumPostageFees,
  computeBuyerProtectionFee,
  bundlePostageDiscountLabel,
  normalizeProductDisplayName,
} from "../../utils/checkoutPricing";
import {
  computeCartBundleTotals,
  defaultBundlePostageConfig,
} from "../../utils/bundlePricing";
import useEnrichedCartPostage from "../../hooks/useEnrichedCartPostage";

// Currency formatter for GBP
const formatCurrency = (value) => {
    if (value === null || value === undefined) return "£0.00";
    return new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: "GBP",
    }).format(Number(value));
};

const Cart = ({ setOpenCart }) => {
  const { cart } = useSelector((state) => state.cart);
  const dispatch = useDispatch();
  const [bundleRuleDiscount, setBundleRuleDiscount] = useState(0);
  const [bundlePostageDiscount, setBundlePostageDiscount] = useState(0);
  const [bundleAppliedShopIds, setBundleAppliedShopIds] = useState([]);
  const [bundlePostageConfig, setBundlePostageConfig] = useState(defaultBundlePostageConfig);
  const [buyerProtectionConfig, setBuyerProtectionConfig] = useState({
    enabled: true,
    fixedFee: 0.7,
    percentage: 2,
  });
  const [bundleWarning, setBundleWarning] = useState({
    open: false,
    item: null,
    text: "",
  });

  const executeRemoveFromCart = (data) => {
    if (!data) return;
    if (data?.isBundleItemFromOffer || data?.bundleOfferId) {
      dispatch(removeBundleDealFromCart(data));
    }
    dispatch(removeFromCart(data));
  };

  const removeFromCartHandler = (data) => {
    const hasAnotherItemFromSameShop = cart.some(
      (item) => item?._id !== data?._id && item?.shopId && item?.shopId === data?.shopId
    );
    const shouldWarn =
      data?.isBundleItemFromOffer || data?.bundleOfferId || hasAnotherItemFromSameShop;

    if (!shouldWarn) {
      executeRemoveFromCart(data);
      return;
    }

    const warningText = data?.isBundleItemFromOffer || data?.bundleOfferId
      ? "This negotiated bundle offer will be cancelled and the remaining items will return to their original prices."
      : "If this item is part of a bundle discount, pricing will be recalculated when the bundle is broken.";

    setBundleWarning({
      open: true,
      item: data,
      text: warningText,
    });
  };

  const confirmBundleRemoval = () => {
    executeRemoveFromCart(bundleWarning.item);
    setBundleWarning({ open: false, item: null, text: "" });
  };

  const { cartWithPostage } = useEnrichedCartPostage(cart);

  // Total price — align with checkout: line subtotal + postage + buyer protection − seller bundle rule discount.
  const cartSubtotalAtLinePrices = sumCartSubtotal(cartWithPostage);
  const postageFeesTotal = sumPostageFees(cartWithPostage);
  const buyerProtectionTotal = computeBuyerProtectionFee(
    cartSubtotalAtLinePrices,
    buyerProtectionConfig
  );

  const preDiscountSubtotal = cartSubtotalAtLinePrices;
  const postageAfterBundle = Math.max(0, postageFeesTotal - bundlePostageDiscount);
  const estimatedToPay = Math.max(
    0,
    cartSubtotalAtLinePrices +
      postageAfterBundle +
      buyerProtectionTotal -
      bundleRuleDiscount
  );

  useEffect(() => {
    const calculateBundleTotals = async () => {
      if (!cart || cart.length === 0) {
        setBundleRuleDiscount(0);
        setBundlePostageDiscount(0);
        setBundleAppliedShopIds([]);
        return;
      }

      const uniqueShopIds = Array.from(
        new Set(cart.map((item) => item.shopId).filter(Boolean))
      );

      if (uniqueShopIds.length === 0) {
        setBundleRuleDiscount(0);
        setBundlePostageDiscount(0);
        setBundleAppliedShopIds([]);
        return;
      }

      try {
        const results = await Promise.all(
          uniqueShopIds.map((id) =>
            axios
              .get(`${server}/shop/get-shop-info/${id}`)
              .then((res) => ({
                id,
                bundleRules: res.data?.shop?.bundleRules || [],
              }))
              .catch(() => ({ id, bundleRules: [] }))
          )
        );

        const shopBundleRulesById = {};
        results.forEach(({ id, bundleRules }) => {
          shopBundleRulesById[String(id)] = bundleRules;
        });

        const totals = computeCartBundleTotals(
          cartWithPostage,
          shopBundleRulesById,
          bundlePostageConfig
        );
        setBundleRuleDiscount(totals.bundleProductDiscount);
        setBundlePostageDiscount(totals.bundlePostageDiscount);
        setBundleAppliedShopIds(totals.appliedShopIds);
      } catch {
        setBundleRuleDiscount(0);
        setBundlePostageDiscount(0);
        setBundleAppliedShopIds([]);
      }
    };

    calculateBundleTotals();
  }, [cart, cartWithPostage, bundlePostageConfig]);

  useEffect(() => {
    const loadPaymentSettings = async () => {
      try {
        const { data } = await axios.get(`${server}/options/public-payment-settings`);
        const bp = data?.settings?.buyerProtection || {};
        setBuyerProtectionConfig({
          enabled: bp.enabled !== false,
          fixedFee: Number(bp.fixedFee ?? 0.7),
          percentage: Number(bp.percentage ?? 2),
        });
        const bpd = data?.settings?.bundlePostageDiscount || {};
        setBundlePostageConfig({
          enabled: bpd.enabled !== false,
          percentage: Number(bpd.percentage ?? 30),
        });
      } catch {
        setBuyerProtectionConfig({
          enabled: true,
          fixedFee: 0.7,
          percentage: 2,
        });
        setBundlePostageConfig(defaultBundlePostageConfig);
      }
    };
    loadPaymentSettings();
  }, []);

  const quantityChangeHandler = (data) => {
    dispatch(addTocart(data));
  };

  return (
    <div className="fixed top-0 left-0 w-full bg-[#0000004b] h-screen z-10" id="side-cart">
      <BundleRemovalWarningModal
        open={bundleWarning.open}
        itemName={bundleWarning?.item?.name || "this item"}
        warningText={bundleWarning.text}
        onClose={() => setBundleWarning({ open: false, item: null, text: "" })}
        onConfirm={confirmBundleRemoval}
      />
      <div className="fixed top-0 right-0 h-full w-[80%] 800px:w-[25%] bg-white flex flex-col overflow-y-scroll justify-between shadow-sm">
        {cart && cart.length == 0 ? (
          <div className="w-full h-screen flex items-center justify-center">
            <div className="flex w-full justify-end pt-5 pr-5 fixed top-3 right-3">
              <RxCross1
                size={25}
                className="cursor-pointer"
                onClick={() => setOpenCart(false)}
              />
            </div>
            <h5>Shopping Cart Is Empty</h5>
          </div>
        ) : (
          <>
            <div>
              <div className="flex w-full justify-end pt-5 pr-5 ">
                <RxCross1
                  size={25}
                  className="cursor-pointer"
                  onClick={() => setOpenCart(false)}
                />
              </div>
              {/* item length */}
              <div className={`${styles.noramlFlex} p-4`}>
                <IoBagHandleOutline size={25} />
                <h5 className="pl-2 text-[20px] font-[500]">
                  {cart && cart.length} items
                </h5>
              </div>

              {/* Cart Single item */}
              <br />
              <div className="w-full border-t">
                {cart &&
                  cart.map((i, index) => {
                    return (
                      <CartSingle
                        data={i}
                        key={index}
                        quantityChangeHandler={quantityChangeHandler}
                        removeFromCartHandler={removeFromCartHandler}
                        bundleAppliedShopIds={bundleAppliedShopIds}
                        onNavigateToProduct={() => setOpenCart(false)}
                      />
                    );
                  })}
              </div>
            </div>

            <div className="px-5 mb-3">
              <div className="mb-3 rounded-lg border border-slate-200 p-3">
                <div className="flex items-center justify-between text-[14px] text-slate-600">
                  <span>Subtotal (items)</span>
                  <span>{formatCurrency(preDiscountSubtotal)}</span>
                </div>
                {postageFeesTotal > 0 && (
                  <div className="mt-1 flex items-center justify-between text-[14px] text-slate-600">
                    <span>Postage</span>
                    <span>{formatCurrency(postageFeesTotal)}</span>
                  </div>
                )}
                {bundlePostageDiscount > 0 && (
                  <div className="mt-1 flex items-center justify-between text-[14px] text-slate-600">
                    <span>{bundlePostageDiscountLabel(bundlePostageConfig.percentage)}</span>
                    <span className="font-medium text-green-600">
                      - {formatCurrency(bundlePostageDiscount)}
                    </span>
                  </div>
                )}
                {buyerProtectionTotal > 0 && (
                  <div className="mt-1 flex items-center justify-between text-[14px] text-slate-600">
                    <span>Buyer protection</span>
                    <span>{formatCurrency(buyerProtectionTotal)}</span>
                  </div>
                )}
                {bundleRuleDiscount > 0 && (
                  <div className="mt-1 flex items-center justify-between text-[14px] text-green-700">
                    <span>Bundle discount</span>
                    <span>- {formatCurrency(bundleRuleDiscount)}</span>
                  </div>
                )}
                <p className="mt-2 border-t border-slate-200 pt-2 text-[11px] leading-snug text-slate-500">
                  Offer or negotiated prices are already included in the item lines. Promo or veteran discounts, if any, apply at checkout.
                </p>
                <div className="mt-2 flex items-center justify-between border-t border-slate-200 pt-2 text-[15px] font-[600] text-[#1f2937]">
                  <span>Estimated to pay</span>
                  <span>{formatCurrency(estimatedToPay)}</span>
                </div>
              </div>
              {/* Check out btn */}
              <Link to="/checkout">
                <div
                  className={`h-[45px] flex items-center justify-center w-[100%] bg-[#38513b] rounded-[5px]`}
                >
                  <h1 className="text-[#fff] text-[18px] font-[600]">
                    Checkout Now
                  </h1>
                </div>
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const CartSingle = ({
  data,
  quantityChangeHandler,
  removeFromCartHandler,
  bundleAppliedShopIds = [],
  onNavigateToProduct,
}) => {
  const navigate = useNavigate();
  const [value, setValue] = useState(data.qty);

  useEffect(() => {
    setValue(data.qty);
  }, [data.qty]);
  const totalPrice = data.discountPrice * value;
  const baseUnitPrice = Number(
    data?.isOfferPriceApplied
      ? data?.listingPriceAtOffer ?? data?.originalPrice ?? data?.discountPrice ?? 0
      : data?.bundleOriginalPrice ?? data?.originalPrice ?? data?.discountPrice ?? 0
  );
  const currentUnitPrice = Number(data?.discountPrice || 0);
  const perUnitSaving = Math.max(0, baseUnitPrice - currentUnitPrice);
  const isBundleOfferItem = !!(data?.isBundleItemFromOffer || data?.bundleOfferId);
  const hasOfferPrice =
    !isBundleOfferItem && !!data?.isOfferPriceApplied && perUnitSaving > 0;
  const isBundleProductApplied =
    !isBundleOfferItem &&
    !!data?.shopId &&
    Array.isArray(bundleAppliedShopIds) &&
    bundleAppliedShopIds.includes(String(data.shopId));

  const increment = (data) => {
    if (data.stock < value) {
      toast.error("Product stock limited!");
    } else {
      setValue(value + 1);
      /* The line `const updateCartData = { ...data, qty: value + 1 };` is creating a new object
            `updateCartData` by spreading the properties of the `data` object and adding a new
            property `qty` with the value of `value + 1`. This is typically used to update the
            quantity of an item in the cart. */
      const updateCartData = { ...data, qty: value + 1 };
      quantityChangeHandler(updateCartData);
    }
  };

  // Decrement
  const decrement = (data) => {
    setValue(value === 1 ? 1 : value - 1);
    const updateCartData = { ...data, qty: value === 1 ? 1 : value - 1 };
    quantityChangeHandler(updateCartData);
  };

  const goToProduct = () => {
    if (!data?._id) return;
    onNavigateToProduct?.();
    navigate(`/product/${data._id}`);
  };

  return (
    <>
      <div className="border-b p-4">
        <div className="w-full flex items-start gap-2">
          <div className="shrink-0">
            <div
              className={`bg-[#38513b] border border-[#38513b73] rounded-full w-[25px] h-[25px] ${styles.noramlFlex} justify-center cursor-pointer`}
              onClick={() => increment(data)}
            >
              <HiPlus size={18} color="#fff" />
            </div>
            <span className="pl-[10px]">{data.qty}</span>
            <div
              className="bg-[#a7abb14f] rounded-full w-[25px] h-[25px] flex items-center justify-center cursor-pointer"
              onClick={() => decrement(data)}
            >
              <HiOutlineMinus size={16} color="#7d879c" />
            </div>
          </div>
          <button
            type="button"
            onClick={goToProduct}
            className="shrink-0 rounded-[5px] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#38513b]"
            aria-label={`View ${normalizeProductDisplayName(data.name)}`}
          >
            <img
              src={`${backend_url}${data?.images[0]}`}
              className="w-[95px] h-[95px] object-contain rounded-[5px]"
              alt={normalizeProductDisplayName(data.name)}
            />
          </button>

          <div className="flex-1 min-w-0 pl-[6px]">
            <button
              type="button"
              onClick={goToProduct}
              className="text-left w-full line-clamp-2 break-words hover:text-[#38513b] hover:underline"
            >
              {normalizeProductDisplayName(data.name)}
            </button>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              {isBundleOfferItem && (
                <span className="rounded-full bg-[#e8f5e9] px-2 py-0.5 text-[10px] font-[600] text-[#1b5e20]">
                  Bundle Offer
                </span>
              )}
              {isBundleProductApplied && (
                <span className="rounded-full bg-[#ede9fe] px-2 py-0.5 text-[10px] font-[600] text-[#5b21b6]">
                  Bundle Product
                </span>
              )}
              {hasOfferPrice && (
                <span className="rounded-full bg-[#e3f2fd] px-2 py-0.5 text-[10px] font-[600] text-[#0d47a1]">
                  Offer Price
                </span>
              )}
            </div>
            <h4 className="font-[400] text-[15px] text-[#00000082]">
              {" "}
              {formatCurrency(data.discountPrice)} x {value}
            </h4>
            {perUnitSaving > 0 && (
              <h5 className="text-[12px] text-[#2e7d32]">
                You save {formatCurrency(perUnitSaving * value)}
              </h5>
            )}
            <h4 className="font-[400] text-[17px] pt-[3px]  text-[#38513b] font-Poppins ">
              {formatCurrency(totalPrice)}
            </h4>
          </div>
          <button
            type="button"
            className="ml-1 mt-1 flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full border border-[#d9ccaf] bg-[#CCBEA1] text-black transition hover:bg-[#c6b48a]"
            onClick={() => removeFromCartHandler(data)}
            aria-label={`Remove ${data?.name || "item"} from cart`}
          >
            <RxCross1 size={14} />
          </button>
        </div>
      </div>
    </>
  );
};

export default Cart;
